import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Calendar } from "./ui/calendar";
import { format, parse } from "date-fns";
import { ChevronDown, Trash2 } from "lucide-react";

export const AppointmentDialog = ({ open, onOpenChange, initial, onSave, onDelete }) => {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(""); // YYYY-MM-DD string
  const [time, setTime] = useState("");
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const timeInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTitle(initial?.title || "");
      setDate(initial?.date || "");
      setTime(initial?.time || "");
      setLocation(initial?.location || "");
      setNote(initial?.note || "");
      setShowCalendar(!initial?.id); // open calendar on new appointment
    }
  }, [open, initial]);

  const isEdit = Boolean(initial?.id);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!title.trim() || !date || !time) return;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        date,
        time,
        location: location.trim(),
        note: note.trim(),
      };
      await onSave?.(payload, initial?.id);
      onOpenChange?.(false);
    } finally {
      setSaving(false);
    }
  };

  const selectedDateObj = (() => {
    if (!date) return undefined;
    try {
      return parse(date, "yyyy-MM-dd", new Date());
    } catch {
      return undefined;
    }
  })();

  const datePretty = selectedDateObj ? format(selectedDateObj, "EEE, d MMM yyyy") : "Pick a date";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md w-[calc(100vw-1.5rem)] sm:w-full p-0 border-none bg-transparent shadow-none max-h-[92vh] overflow-y-auto"
        data-testid="appointment-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="glass-card rounded-2xl p-6 sm:p-8">
          <DialogHeader className="space-y-2 text-left">
            <p className="eyebrow">{isEdit ? "Edit reservation" : "New reservation"}</p>
            <DialogTitle className="font-serif text-2xl sm:text-3xl font-medium text-[#F5F5F5] leading-tight">
              {isEdit ? "Refine the details" : "Reserve a moment"}
            </DialogTitle>
            <DialogDescription className="text-[#8E8E93] text-sm">
              {isEdit ? "Update the appointment details below." : "Add a new appointment to your calendar."}
            </DialogDescription>
          </DialogHeader>

          <form className="mt-6 space-y-5" onSubmit={handleSave}>
            <div>
              <label className="eyebrow text-[10px] block mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Dinner at Le Bernardin"
                className="input-luxe"
                required
                data-testid="appt-title-input"
              />
            </div>

            {/* Date — tap to expand inline calendar */}
            <div>
              <label className="eyebrow text-[10px] block mb-1">Date</label>
              <button
                type="button"
                onClick={() => setShowCalendar((v) => !v)}
                onTouchEnd={(e) => {
                  // Ensure tap registers on iOS Safari inside Radix Dialog scroll container
                  e.preventDefault();
                  setShowCalendar((v) => !v);
                }}
                className="input-luxe flex items-center justify-between text-left"
                style={{
                  WebkitAppearance: "none",
                  appearance: "none",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "rgba(212, 175, 55, 0.18)",
                  touchAction: "manipulation",
                }}
                data-testid="appt-date-trigger"
                aria-expanded={showCalendar}
              >
                <span className={date ? "text-[#F5F5F5]" : "text-[#6b6b73]"}>{datePretty}</span>
                <ChevronDown
                  size={16}
                  className={`text-[#D4AF37] transition-transform duration-200 ${showCalendar ? "rotate-180" : ""}`}
                />
              </button>
              {showCalendar && (
                <div
                  className="mt-3 rounded-xl border border-[#D4AF37]/20 bg-[#0d0d12] p-2 fade-up"
                  data-testid="appt-date-calendar"
                >
                  <Calendar
                    mode="single"
                    selected={selectedDateObj}
                    onSelect={(d) => {
                      if (d) {
                        setDate(format(d, "yyyy-MM-dd"));
                        setShowCalendar(false);
                        // Auto-focus time input on small screens after picking date
                        setTimeout(() => {
                          timeInputRef.current?.focus?.();
                        }, 100);
                      }
                    }}
                    initialFocus
                  />
                </div>
              )}
            </div>

            <div>
              <label className="eyebrow text-[10px] block mb-1">Time</label>
              <input
                ref={timeInputRef}
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="input-luxe"
                required
                data-testid="appt-time-input"
              />
            </div>

            <div>
              <label className="eyebrow text-[10px] block mb-1">Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Manhattan, NY"
                className="input-luxe"
                data-testid="appt-location-input"
              />
            </div>

            <div>
              <label className="eyebrow text-[10px] block mb-1">Note</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Anything to remember…"
                className="input-luxe"
                rows={3}
                data-testid="appt-note-input"
              />
            </div>

            <DialogFooter className="!flex !flex-row !justify-between gap-3 pt-4 mt-2 border-t border-[#D4AF37]/10">
              {isEdit ? (
                <button
                  type="button"
                  onClick={() => {
                    onDelete?.(initial.id);
                    onOpenChange?.(false);
                  }}
                  className="inline-flex items-center gap-1.5 text-[#FF453A]/90 text-xs uppercase tracking-[0.2em] hover:text-[#FF453A] transition-colors"
                  data-testid="appt-delete-btn"
                >
                  <Trash2 size={13} /> Delete
                </button>
              ) : <span />}

              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => onOpenChange?.(false)}
                  className="btn-ghost-gold rounded-full px-4 sm:px-5 py-2.5 text-xs tracking-[0.2em] uppercase"
                  data-testid="appt-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !title || !date || !time}
                  className="btn-gold rounded-full px-5 sm:px-6 py-2.5 text-xs tracking-[0.2em] uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="appt-save-btn"
                >
                  {saving ? "Saving" : isEdit ? "Update" : "Reserve"}
                </button>
              </div>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentDialog;
